import { createTheme } from '@mui/material/styles'

import components from '../utils/theme-components' // Your custom components
import { black, greyDarkest, palette, primaryColor } from './colorPalette'

// In MUI v7, extending TypographyVariants is necessary to add custom typography variants like `tabBarCurrent` and `tabBarDefault`.
// This ensures TypeScript recognizes these custom variants when used in components.
declare module '@mui/material/styles' {
  interface TypographyVariants {
    tabBarCurrent: React.CSSProperties
    tabBarDefault: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    tabBarCurrent?: React.CSSProperties
    tabBarDefault: React.CSSProperties
  }
}

// Create theme
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Fira Sans';
          font-style: normal;
          font-weight: 400;
          src: url('/assets/fonts/fira-sans-v16-latin-regular.eot');
          src: local(''),
               url('/assets/fonts/fira-sans-v16-latin-regular.eot?#iefix') format('embedded-opentype'),
               url('/assets/fonts/fira-sans-v16-latin-regular.woff2') format('woff2'),
               url('/assets/fonts/fira-sans-v16-latin-regular.woff') format('woff'),
               url('/assets/fonts/fira-sans-v16-latin-regular.ttf') format('truetype'),
               url('/assets/fonts/fira-sans-v16-latin-regular.svg#FiraSans') format('svg');
        }
        @font-face {
          font-family: 'Fira Sans';
          font-style: italic;
          font-weight: 400;
          src: url('/assets/fonts/fira-sans-v16-latin-italic.eot');
          src: local(''),
               url('/assets/fonts/fira-sans-v16-latin-italic.eot?#iefix') format('embedded-opentype'),
               url('/assets/fonts/fira-sans-v16-latin-italic.woff2') format('woff2'),
               url('/assets/fonts/fira-sans-v16-latin-italic.woff') format('woff'),
               url('/assets/fonts/fira-sans-v16-latin-italic.ttf') format('truetype'),
               url('/assets/fonts/fira-sans-v16-latin-italic.svg#FiraSans') format('svg');
        }
        @font-face {
          font-family: 'Fira Sans';
          font-style: normal;
          font-weight: 500;
          src: url('/assets/fonts/fira-sans-v16-latin-500.eot');
          src: local(''),
               url('/assets/fonts/fira-sans-v16-latin-500.eot?#iefix') format('embedded-opentype'),
               url('/assets/fonts/fira-sans-v16-latin-500.woff2') format('woff2'),
               url('/assets/fonts/fira-sans-v16-latin-500.woff') format('woff'),
               url('/assets/fonts/fira-sans-v16-latin-500.ttf') format('truetype'),
               url('/assets/fonts/fira-sans-v16-latin-500.svg#FiraSans') format('svg');
        }
        @font-face {
          font-family: 'Fira Sans';
          font-style: normal;
          font-weight: 700;
          src: url('/assets/fonts/fira-sans-v16-latin-700.eot');
          src: local(''),
               url('/assets/fonts/fira-sans-v16-latin-700.eot?#iefix') format('embedded-opentype'),
               url('/assets/fonts/fira-sans-v16-latin-700.woff2') format('woff2'),
               url('/assets/fonts/fira-sans-v16-latin-700.woff') format('woff'),
               url('/assets/fonts/fira-sans-v16-latin-700.ttf') format('truetype'),
               url('/assets/fonts/fira-sans-v16-latin-700.svg#FiraSans') format('svg');
        }
      `
    },
    ...components
  },
  palette,
  shape: {
    borderRadius: 4
  },
  typography: {
    htmlFontSize: 16,
    fontFamily: '"Fira Sans", sans-serif',
    fontSize: 16,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 'bold',
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: 'normal'
    },
    h2: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '2rem',
      lineHeight: 1.1875
    },
    h3: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '3rem',
      lineHeight: 1.167,
      letterSpacing: '0em'
    },
    h4: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 'normal',
      fontSize: '1rem',
      lineHeight: 1.25,
      letterSpacing: 'normal',
      color: greyDarkest
    },
    h5: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: 1.334,
      letterSpacing: '0em'
    },
    h6: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      letterSpacing: '0.0075em'
    },
    tabBarCurrent: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '1.25rem',
      lineHeight: 1,
      color: primaryColor
    },
    tabBarDefault: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '1.25rem',
      lineHeight: 1,
      color: primaryColor
    },
    subtitle1: {
      fontFamily: '"Fira Sans", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 400,
      lineHeight: 1.2,
      color: black
    },
    subtitle2: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.57,
      letterSpacing: '0.00714em'
    },
    body1: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em'
    },
    body2: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      letterSpacing: '0.01071em'
    },
    button: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'uppercase'
    },
    caption: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.66,
      letterSpacing: '0.03333em'
    },
    overline: {
      fontFamily: '"Fira Sans", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase'
    }
  }
})

export default theme
